import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical, Eye, Settings,
  Type, Mail, Phone, MapPin, FileText, CheckSquare, List, Loader2, Video, Users, Info, Euro, Clock, ChevronDown, ChevronUp, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { companyApi } from '../../lib/api';

// Default fields configuration
const DEFAULT_FIELDS = [
  { field_id: 'name', label: 'Nom complet', type: 'text', required: true, placeholder: 'Jean Dupont', step: 'info' },
  { field_id: 'email', label: 'Email', type: 'email', required: true, placeholder: 'jean@email.fr', step: 'info' },
  { field_id: 'phone', label: 'Téléphone', type: 'tel', required: false, placeholder: '06 12 34 56 78', step: 'info' },
  { field_id: 'address', label: 'Adresse', type: 'textarea', required: false, placeholder: 'Votre adresse...', step: 'info' },
  { field_id: 'notes', label: 'Notes', type: 'textarea', required: false, placeholder: 'Informations complémentaires...', step: 'info' },
];

// Default steps configuration
const DEFAULT_STEPS = [
  { step_id: 'service', title: 'Service', description: 'Choisissez le service souhaité', enabled: true, order: 0 },
  { step_id: 'datetime', title: 'Date & Heure', description: 'Sélectionnez une date et un horaire', enabled: true, order: 1 },
  { step_id: 'info', title: 'Informations', description: 'Vos coordonnées', enabled: true, order: 2 },
  { step_id: 'confirm', title: 'Confirmation', description: 'Vérifiez et confirmez', enabled: true, order: 3 },
];

// Field types
const FIELD_TYPES = [
  { value: 'text', label: 'Texte court' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Téléphone' },
  { value: 'textarea', label: 'Zone de texte' },
  { value: 'select', label: 'Liste déroulante' },
  { value: 'checkbox', label: 'Case à cocher' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Nombre' },
];

const FIELD_ICONS = {
  text: Type,
  email: Mail,
  tel: Phone,
  textarea: FileText,
  select: List,
  checkbox: CheckSquare,
  date: Type,
  number: Type,
};

export default function WidgetEditor() {
  const { widgetId } = useParams();
  const navigate = useNavigate();
  const isNew = widgetId === 'new';
  
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [slotTypes, setSlotTypes] = useState([]); // Types de créneaux
  const [existingWidgets, setExistingWidgets] = useState([]); // For dispatcher config
  const [activeTab, setActiveTab] = useState('general');
  const [newlyAddedFieldId, setNewlyAddedFieldId] = useState(null); // Track newly added field for highlight
  const newFieldRef = useRef(null); // Ref for scrolling to new field
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false); // Réglages avancés
  
  // Onglets avancés - si on les cache, basculer sur Général
  useEffect(() => {
    if (!showAdvancedSettings && ['style', 'questions', 'terms'].includes(activeTab)) {
      setActiveTab('general');
    }
  }, [showAdvancedSettings]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const [widget, setWidget] = useState({
    widget_type: 'punctual',
    name: '',
    description: '',
    booking_mode: 'approval',
    allow_payment: false,
    skip_dispatcher: false, // Skip the choice screen and go directly to booking form
    primary_color: '#2F5233',
    secondary_color: '#D97757',
    bg_color: '#F9F8F6',
    text_color: '#1a1a1a',
    enabled_services: [],
    enabled_categories: [], // NEW: Selected category IDs
    enabled_slot_types: [], // Types de créneaux activés pour ce widget
    require_slot_selection: true, // Le client doit-il choisir un créneau ?
    fields: DEFAULT_FIELDS,
    steps: DEFAULT_STEPS,
    terms_enabled: false,
    terms_text: '',
    terms_link: '',
    show_logo: true,
    hide_duration_choice: false, // Hide duration choice in punctual/recurring widgets
    is_active: true, // Widget is active by default
    show_in_client_space: true, // Visible in connected client space by default
    // Dispatcher options (for dispatcher widget type)
    dispatcher_options: [],
    recurring_widget_id: null,
    punctual_widget_id: null,
    // Appointment specific settings
    appointment_modes: ['presential', 'video'], // physical, video - renamed from appointment_types
    appointment_location: '',
    appointment_video_link: '',
    // Appointment service types (ex: RDV Devis 30min 0€, RDV Étude 60min 50€)
    appointment_service_types: [
      { id: 'default', name: 'Consultation', duration: 60, price: 0 }
    ],
    // Collaborateurs assignés au widget RDV
    assigned_collaborator_ids: [],
    // Custom work hours for this widget
    use_custom_hours: false,
    custom_work_hours: [
      { day_of_week: 0, start_time: '09:00', end_time: '17:30', is_active: true },
      { day_of_week: 1, start_time: '09:00', end_time: '17:30', is_active: true },
      { day_of_week: 2, start_time: '09:00', end_time: '17:30', is_active: true },
      { day_of_week: 3, start_time: '09:00', end_time: '17:30', is_active: true },
      { day_of_week: 4, start_time: '09:00', end_time: '17:30', is_active: true },
      { day_of_week: 5, start_time: '09:00', end_time: '12:00', is_active: false },
      { day_of_week: 6, start_time: '09:00', end_time: '12:00', is_active: false },
    ],
    // Integration status
    integration_url: '',
    integration_name: '',
    confirmation_title: 'Merci pour votre réservation !',
    confirmation_message: 'Votre demande a bien été enregistrée. Vous recevrez une confirmation par email.',
    submit_button_text: 'Confirmer la réservation',
    intro_text: '',
    // Pricing options
    pricing_mode: 'hourly', // 'hourly' | 'per_service' | 'per_slot_type'
    hourly_rate: 35,
    service_prices: {}, // { service_id: price }
    slot_type_prices: {}, // { slot_type_id: price } - prix fixe par créneau au niveau widget
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, categoriesRes, collaboratorsRes, widgetsRes, slotTypesRes] = await Promise.all([
          companyApi.getServices(),
          companyApi.getServiceCategories(),
          companyApi.getCollaborators(),
          companyApi.getWidgets(),
          companyApi.getSlotTypes()
        ]);
        setServices(servicesRes.data);
        setCategories(categoriesRes.data || []);
        setCollaborators(collaboratorsRes.data || []);
        setExistingWidgets(widgetsRes.data || []); // For dispatcher config
        setSlotTypes(slotTypesRes.data || []);
        
        if (!isNew) {
          const existingWidget = widgetsRes.data.find(w => w.id === widgetId);
          if (existingWidget) {
            setWidget({
              ...widget,
              ...existingWidget,
              fields: existingWidget.fields || DEFAULT_FIELDS,
              steps: existingWidget.steps || DEFAULT_STEPS,
              assigned_collaborator_ids: existingWidget.assigned_collaborator_ids || [],
              enabled_categories: existingWidget.enabled_categories || [],
              enabled_slot_types: existingWidget.enabled_slot_types || [],
              require_slot_selection: existingWidget.require_slot_selection !== false,
              use_custom_hours: existingWidget.use_custom_hours || false,
              custom_work_hours: existingWidget.custom_work_hours || widget.custom_work_hours,
              dispatcher_options: existingWidget.dispatcher_options || [],
              pricing_mode: existingWidget.pricing_mode || 'hourly',
              hourly_rate: existingWidget.hourly_rate ?? 35,
              service_prices: existingWidget.service_prices || {},
              slot_type_prices: existingWidget.slot_type_prices || {},
            });
          }
        }
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [widgetId, isNew]);

  const handleSave = async () => {
    if (!widget.name) {
      toast.error('Le nom du widget est obligatoire');
      return;
    }
    
    setIsSaving(true);
    try {
      if (isNew) {
        await companyApi.createWidget(widget);
        toast.success('Widget créé');
      } else {
        await companyApi.updateWidget(widgetId, widget);
        toast.success('Widget mis à jour');
      }
      navigate('/company/widgets');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (index, updates) => {
    const newFields = [...widget.fields];
    newFields[index] = { ...newFields[index], ...updates };
    setWidget({ ...widget, fields: newFields });
  };

  const addField = () => {
    const newFieldId = `custom_${Date.now()}`;
    const newField = {
      field_id: newFieldId,
      label: 'Nouveau champ',
      type: 'text',
      required: false,
      placeholder: '',
      step: 'info', // Default to info step
    };
    setWidget({ ...widget, fields: [...widget.fields, newField] });
    setNewlyAddedFieldId(newFieldId);
    
    // Switch to questions tab and scroll to new field after render
    setActiveTab('questions');
    setTimeout(() => {
      if (newFieldRef.current) {
        newFieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Remove highlight after 3 seconds
      setTimeout(() => setNewlyAddedFieldId(null), 3000);
    }, 100);
  };

  const removeField = (index) => {
    const newFields = widget.fields.filter((_, i) => i !== index);
    setWidget({ ...widget, fields: newFields });
  };

  const updateStep = (index, updates) => {
    const newSteps = [...widget.steps];
    newSteps[index] = { ...newSteps[index], ...updates };
    setWidget({ ...widget, steps: newSteps });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/company/widgets')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-semibold">
              {isNew ? 'Nouveau Widget' : 'Modifier le Widget'}
            </h1>
            <p className="text-muted-foreground">Personnalisez votre formulaire de réservation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/company/widgets')}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={isSaving} className="rounded-full">
            {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Tabs - Reorganized for better workflow UX */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Onglets principaux : Général, Services, Parcours, Tarifs */}
        <TabsList className="grid w-full grid-cols-4 h-auto gap-1">
          <TabsTrigger value="general" data-testid="widget-tab-general" className="text-xs lg:text-sm py-2">Général</TabsTrigger>
          <TabsTrigger value="services" data-testid="widget-tab-services" className="text-xs lg:text-sm py-2">Services</TabsTrigger>
          <TabsTrigger value="steps" data-testid="widget-tab-steps" className="text-xs lg:text-sm py-2">Parcours</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="widget-tab-pricing" className="text-xs lg:text-sm py-2">Tarifs</TabsTrigger>
        </TabsList>

        {/* Toggle "Réglages avancés" */}
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="widget-toggle-advanced"
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            className="text-muted-foreground hover:text-foreground"
          >
            <Settings className="w-4 h-4 mr-2" />
            Réglages avancés
            {showAdvancedSettings ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </Button>
        </div>

        {/* Onglets avancés : Apparence, Questions, CGV (cachés par défaut) */}
        {showAdvancedSettings && (
          <TabsList className="grid w-full grid-cols-3 h-auto gap-1 mt-2 bg-muted/50">
            <TabsTrigger value="style" data-testid="widget-tab-style" className="text-xs lg:text-sm py-2">Apparence</TabsTrigger>
            <TabsTrigger value="questions" data-testid="widget-tab-questions" className="text-xs lg:text-sm py-2">Questions</TabsTrigger>
            <TabsTrigger value="terms" data-testid="widget-tab-terms" className="text-xs lg:text-sm py-2">CGV</TabsTrigger>
          </TabsList>
        )}

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type de widget</Label>
                  <Select
                    value={widget.widget_type}
                    onValueChange={(value) => setWidget({ ...widget, widget_type: value })}
                    disabled={!isNew}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dispatcher">Dispatcher (écran de choix)</SelectItem>
                      <SelectItem value="punctual">Demande ponctuelle</SelectItem>
                      <SelectItem value="recurring">Abonnement / Récurrent</SelectItem>
                      <SelectItem value="appointment">Prise de RDV</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {widget.widget_type === 'dispatcher' && "Écran de choix entre plusieurs types de demandes"}
                    {widget.widget_type === 'punctual' && "Pour des demandes de prestation à la demande"}
                    {widget.widget_type === 'recurring' && "Pour des abonnements avec prestations régulières"}
                    {widget.widget_type === 'appointment' && "Pour des rendez-vous physiques ou en visioconférence"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Mode de réservation</Label>
                  <Select
                    value={widget.booking_mode}
                    onValueChange={(value) => setWidget({ ...widget, booking_mode: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Confirmation automatique</SelectItem>
                      <SelectItem value="approval">Validation manuelle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Option accès direct - uniquement pour widgets ponctuels */}
              {widget.widget_type === 'punctual' && (
                <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Accès direct au formulaire</Label>
                    <p className="text-xs text-muted-foreground">
                      Désactiver l'écran de choix "Entretien / Intervention" et afficher directement le formulaire de réservation
                    </p>
                  </div>
                  <Switch
                    checked={widget.skip_dispatcher || false}
                    onCheckedChange={(checked) => setWidget({ ...widget, skip_dispatcher: checked })}
                  />
                </div>
              )}

              {/* Configuration du Dispatcher - options de menu */}
              {widget.widget_type === 'dispatcher' && (
                <div className="space-y-4 p-4 rounded-lg border bg-amber-50/50 border-amber-200">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-amber-600" />
                    <Label className="text-sm font-medium text-amber-800">Options du Menu Dispatcher</Label>
                  </div>
                  <p className="text-xs text-amber-700">
                    Configurez les options qui apparaîtront dans le menu de sélection. 
                    Chaque option redirige vers un widget spécifique (Ponctuel, Abonnement, ou RDV).
                  </p>
                  
                  {/* Liste des options */}
                  <div className="space-y-3">
                    {(widget.dispatcher_options || []).map((option, index) => (
                      <div key={option.id || index} className="p-3 bg-white rounded-lg border shadow-sm">
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Titre *</Label>
                            <Input
                              value={option.title || ''}
                              onChange={(e) => {
                                const newOptions = [...(widget.dispatcher_options || [])];
                                newOptions[index] = { ...newOptions[index], title: e.target.value };
                                setWidget({ ...widget, dispatcher_options: newOptions });
                              }}
                              placeholder="Entretien de jardin"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Sous-titre</Label>
                            <Input
                              value={option.subtitle || ''}
                              onChange={(e) => {
                                const newOptions = [...(widget.dispatcher_options || [])];
                                newOptions[index] = { ...newOptions[index], subtitle: e.target.value };
                                setWidget({ ...widget, dispatcher_options: newOptions });
                              }}
                              placeholder="Tonte, taille, désherbage"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Icône</Label>
                            <Select
                              value={option.icon || 'leaf'}
                              onValueChange={(value) => {
                                const newOptions = [...(widget.dispatcher_options || [])];
                                newOptions[index] = { ...newOptions[index], icon: value };
                                setWidget({ ...widget, dispatcher_options: newOptions });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="leaf">Feuille</SelectItem>
                                <SelectItem value="repeat">Répéter</SelectItem>
                                <SelectItem value="wrench">Outil</SelectItem>
                                <SelectItem value="scissors">Ciseaux</SelectItem>
                                <SelectItem value="lightbulb">Ampoule</SelectItem>
                                <SelectItem value="tree">Arbre</SelectItem>
                                <SelectItem value="flower">Fleur</SelectItem>
                                <SelectItem value="calendar">Calendrier</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Couleur</Label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={option.color || '#22C55E'}
                                onChange={(e) => {
                                  const newOptions = [...(widget.dispatcher_options || [])];
                                  newOptions[index] = { ...newOptions[index], color: e.target.value };
                                  setWidget({ ...widget, dispatcher_options: newOptions });
                                }}
                                className="w-8 h-8 rounded border cursor-pointer"
                              />
                              <Input
                                value={option.color || '#22C55E'}
                                onChange={(e) => {
                                  const newOptions = [...(widget.dispatcher_options || [])];
                                  newOptions[index] = { ...newOptions[index], color: e.target.value };
                                  setWidget({ ...widget, dispatcher_options: newOptions });
                                }}
                                className="flex-1"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Type de widget</Label>
                            <Select
                              value={option.target_widget_type || 'punctual'}
                              onValueChange={(value) => {
                                const newOptions = [...(widget.dispatcher_options || [])];
                                newOptions[index] = { ...newOptions[index], target_widget_type: value, target_widget_id: '' };
                                setWidget({ ...widget, dispatcher_options: newOptions });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="recurring">Abonnement</SelectItem>
                                <SelectItem value="punctual">Ponctuel</SelectItem>
                                <SelectItem value="appointment">Demande RDV</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Widget cible *</Label>
                            <Select
                              value={option.target_widget_id || 'none'}
                              onValueChange={(value) => {
                                const newOptions = [...(widget.dispatcher_options || [])];
                                newOptions[index] = { ...newOptions[index], target_widget_id: value === 'none' ? '' : value };
                                setWidget({ ...widget, dispatcher_options: newOptions });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionner..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">-- Aucun --</SelectItem>
                                {existingWidgets?.filter(w => w.widget_type === (option.target_widget_type || 'punctual') && w.id !== widget.id).map(w => (
                                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Badge (optionnel)</Label>
                            <Input
                              value={option.badge_text || ''}
                              onChange={(e) => {
                                const newOptions = [...(widget.dispatcher_options || [])];
                                newOptions[index] = { ...newOptions[index], badge_text: e.target.value };
                                setWidget({ ...widget, dispatcher_options: newOptions });
                              }}
                              placeholder="Avance immédiate (-50%)"
                            />
                          </div>
                        </div>
                        
                        {/* Couleur du badge */}
                        {option.badge_text && (
                          <div className="mb-3">
                            <Label className="text-xs">Couleur du badge</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="color"
                                value={option.badge_color || '#F59E0B'}
                                onChange={(e) => {
                                  const newOptions = [...(widget.dispatcher_options || [])];
                                  newOptions[index] = { ...newOptions[index], badge_color: e.target.value };
                                  setWidget({ ...widget, dispatcher_options: newOptions });
                                }}
                                className="w-8 h-8 rounded border cursor-pointer"
                              />
                              <span className="text-xs">{option.badge_color || '#F59E0B'}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Supprimer l'option */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newOptions = (widget.dispatcher_options || []).filter((_, i) => i !== index);
                            setWidget({ ...widget, dispatcher_options: newOptions });
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Ajouter une option */}
                  <Button
                    variant="outline"
                    onClick={() => {
                      const newOption = {
                        id: `opt_${Date.now()}`,
                        title: '',
                        subtitle: '',
                        icon: 'leaf',
                        color: '#22C55E',
                        badge_text: '',
                        badge_color: '#F59E0B',
                        target_widget_id: '',
                        target_widget_type: 'punctual',
                        order: (widget.dispatcher_options || []).length
                      };
                      setWidget({ ...widget, dispatcher_options: [...(widget.dispatcher_options || []), newOption] });
                    }}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Ajouter une option
                  </Button>
                  
                  {/* Ancien format (rétrocompatibilité) */}
                  {(widget.dispatcher_options || []).length === 0 && (
                    <div className="mt-4 pt-4 border-t border-amber-200">
                      <p className="text-xs text-amber-600 mb-3">
                        Ou utilisez la configuration simplifiée (2 options max) :
                      </p>
                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm">Widget Abonnement (Entretien Récurrent)</Label>
                          <Select
                            value={widget.recurring_widget_id || 'none'}
                            onValueChange={(value) => setWidget({ ...widget, recurring_widget_id: value === 'none' ? null : value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un widget abonnement" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Aucun (désactivé)</SelectItem>
                              {existingWidgets?.filter(w => w.widget_type === 'recurring' && w.id !== widget.id).map(w => (
                                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-sm">Widget Ponctuel (Intervention à la demande)</Label>
                          <Select
                            value={widget.punctual_widget_id || 'none'}
                            onValueChange={(value) => setWidget({ ...widget, punctual_widget_id: value === 'none' ? null : value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un widget ponctuel" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Aucun (désactivé)</SelectItem>
                              {existingWidgets?.filter(w => w.widget_type === 'punctual' && w.id !== widget.id).map(w => (
                                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Nom du widget *</Label>
                <Input
                  value={widget.name}
                  onChange={(e) => setWidget({ ...widget, name: e.target.value })}
                  placeholder="Ma réservation en ligne"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={widget.description || ''}
                  onChange={(e) => setWidget({ ...widget, description: e.target.value })}
                  placeholder="Description affichée aux visiteurs..."
                />
              </div>

              <div className="space-y-2">
                <Label>Texte d'introduction</Label>
                <Textarea
                  value={widget.intro_text || ''}
                  onChange={(e) => setWidget({ ...widget, intro_text: e.target.value })}
                  placeholder="Bienvenue ! Réservez votre créneau en quelques clics..."
                />
              </div>

              {/* Statut d'intégration */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Label className="text-base">Statut d'intégration</Label>
                  {widget.integration_url && (
                    <Badge variant="outline" className="text-xs text-green-600 border-green-300 bg-green-50 flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      En fonction
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Nom de l'intégration</Label>
                    <Input
                      value={widget.integration_name || ''}
                      onChange={(e) => setWidget({ ...widget, integration_name: e.target.value })}
                      placeholder="Ex: Site vitrine, Landing page..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">URL d'intégration</Label>
                    <Input
                      value={widget.integration_url || ''}
                      onChange={(e) => setWidget({ ...widget, integration_url: e.target.value })}
                      placeholder="https://monsite.fr/reservation"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Renseignez l'URL où ce widget est intégré pour activer l'indicateur "En fonction"
                </p>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Paiement en ligne</Label>
                  <p className="text-xs text-muted-foreground">Permettre le paiement lors de la réservation</p>
                </div>
                <Switch
                  checked={widget.allow_payment}
                  onCheckedChange={(checked) => setWidget({ ...widget, allow_payment: checked })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Appointment Specific Settings */}
          {widget.widget_type === 'appointment' && (
            <Card>
              <CardHeader>
                <CardTitle>Configuration du RDV</CardTitle>
                <CardDescription>Définissez les types de RDV que vous proposez</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Modes de RDV (présentiel / visio) */}
                <div className="space-y-3">
                  <Label>Modes de RDV disponibles</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div 
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        (widget.appointment_modes || []).includes('presential') 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => {
                        const modes = widget.appointment_modes || [];
                        const newModes = modes.includes('presential') 
                          ? modes.filter(t => t !== 'presential')
                          : [...modes, 'presential'];
                        setWidget({ ...widget, appointment_modes: newModes });
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-5 h-5 text-primary" />
                        <span className="font-semibold">En présentiel</span>
                      </div>
                      <p className="text-sm text-muted-foreground">RDV physique dans vos locaux</p>
                    </div>
                    <div 
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        (widget.appointment_modes || []).includes('video') 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => {
                        const modes = widget.appointment_modes || [];
                        const newModes = modes.includes('video') 
                          ? modes.filter(t => t !== 'video')
                          : [...modes, 'video'];
                        setWidget({ ...widget, appointment_modes: newModes });
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Video className="w-5 h-5 text-blue-500" />
                        <span className="font-semibold">En visioconférence</span>
                      </div>
                      <p className="text-sm text-muted-foreground">RDV à distance en visio</p>
                    </div>
                  </div>
                </div>

                {(widget.appointment_modes || []).includes('presential') && (
                  <div className="space-y-2">
                    <Label>Adresse du RDV (présentiel)</Label>
                    <Input
                      value={widget.appointment_location || ''}
                      onChange={(e) => setWidget({ ...widget, appointment_location: e.target.value })}
                      placeholder="123 rue Example, 75000 Paris"
                    />
                  </div>
                )}

                {(widget.appointment_modes || []).includes('video') && (
                  <div className="space-y-2">
                    <Label>Lien de visioconférence (optionnel)</Label>
                    <Input
                      value={widget.appointment_video_link || ''}
                      onChange={(e) => setWidget({ ...widget, appointment_video_link: e.target.value })}
                      placeholder="https://meet.google.com/... ou https://zoom.us/..."
                    />
                    <p className="text-xs text-muted-foreground">Si vide, le lien sera envoyé après confirmation</p>
                  </div>
                )}

                {/* Sélection des collaborateurs/commerciaux */}
                <div className="space-y-3 border-t pt-4">
                  <div>
                    <Label className="text-base flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Commerciaux disponibles
                    </Label>
                    <p className="text-sm text-muted-foreground">Sélectionnez les collaborateurs qui peuvent recevoir des RDV via ce widget</p>
                  </div>
                  
                  {collaborators.filter(c => c.is_active && c.role === 'commercial').length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {collaborators.filter(c => c.is_active && c.role === 'commercial').map(collab => (
                        <div 
                          key={collab.id}
                          className={`p-3 border-2 rounded-lg cursor-pointer transition-all flex items-center gap-3 ${
                            (widget.assigned_collaborator_ids || []).includes(collab.id)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => {
                            const ids = widget.assigned_collaborator_ids || [];
                            const newIds = ids.includes(collab.id)
                              ? ids.filter(id => id !== collab.id)
                              : [...ids, collab.id];
                            setWidget({ ...widget, assigned_collaborator_ids: newIds });
                          }}
                        >
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                            {collab.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{collab.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{collab.email}</p>
                          </div>
                          <Checkbox 
                            checked={(widget.assigned_collaborator_ids || []).includes(collab.id)}
                            className="pointer-events-none"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 border border-dashed rounded-lg text-center text-muted-foreground">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucun commercial disponible</p>
                      <p className="text-xs mt-1">Ajoutez des collaborateurs avec le rôle "Commercial" dans la section Collaborateurs</p>
                    </div>
                  )}
                  
                  {(widget.assigned_collaborator_ids || []).length > 0 && (
                    <p className="text-sm text-primary">
                      {(widget.assigned_collaborator_ids || []).length} commercial{(widget.assigned_collaborator_ids || []).length > 1 ? 'x' : ''} sélectionné{(widget.assigned_collaborator_ids || []).length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                {/* Types de RDV personnalisés */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Types de RDV proposés</Label>
                      <p className="text-sm text-muted-foreground">Définissez vos prestations avec durée et tarif</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const types = widget.appointment_service_types || [];
                        const newType = {
                          id: `type_${Date.now()}`,
                          name: '',
                          duration: 60,
                          price: 0
                        };
                        setWidget({ ...widget, appointment_service_types: [...types, newType] });
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Ajouter un type
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {(widget.appointment_service_types || []).map((serviceType, index) => (
                      <div key={serviceType.id} className="p-4 border rounded-xl bg-muted/30 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Type #{index + 1}</span>
                          {(widget.appointment_service_types || []).length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                const types = widget.appointment_service_types.filter(t => t.id !== serviceType.id);
                                setWidget({ ...widget, appointment_service_types: types });
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Nom du RDV</Label>
                            <Input
                              value={serviceType.name}
                              onChange={(e) => {
                                const types = widget.appointment_service_types.map(t => 
                                  t.id === serviceType.id ? { ...t, name: e.target.value } : t
                                );
                                setWidget({ ...widget, appointment_service_types: types });
                              }}
                              placeholder="Ex: RDV Devis, Consultation..."
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Durée</Label>
                            <Select
                              value={String(serviceType.duration)}
                              onValueChange={(value) => {
                                const types = widget.appointment_service_types.map(t => 
                                  t.id === serviceType.id ? { ...t, duration: parseInt(value) } : t
                                );
                                setWidget({ ...widget, appointment_service_types: types });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="15">15 min</SelectItem>
                                <SelectItem value="30">30 min</SelectItem>
                                <SelectItem value="45">45 min</SelectItem>
                                <SelectItem value="60">1h</SelectItem>
                                <SelectItem value="90">1h30</SelectItem>
                                <SelectItem value="120">2h</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Prix (€)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="5"
                              value={serviceType.price}
                              onChange={(e) => {
                                const types = widget.appointment_service_types.map(t => 
                                  t.id === serviceType.id ? { ...t, price: parseFloat(e.target.value) || 0 } : t
                                );
                                setWidget({ ...widget, appointment_service_types: types });
                              }}
                              placeholder="0 = Gratuit"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {(widget.appointment_service_types || []).length === 0 && (
                    <div className="p-4 border-2 border-dashed rounded-xl text-center text-muted-foreground">
                      <p>Aucun type de RDV défini</p>
                      <p className="text-sm">Cliquez sur "Ajouter un type" pour commencer</p>
                    </div>
                  )}
                </div>

                {/* Horaires personnalisés */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Horaires de disponibilité
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Définissez les horaires spécifiques pour ce widget (différents des horaires généraux de l'entreprise)
                      </p>
                    </div>
                    <Switch
                      checked={widget.use_custom_hours}
                      onCheckedChange={(checked) => setWidget({ ...widget, use_custom_hours: checked })}
                    />
                  </div>

                  {widget.use_custom_hours && (
                    <div className="space-y-2 mt-3">
                      {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((dayName, index) => {
                        const dayHours = widget.custom_work_hours?.find(h => h.day_of_week === index) || {
                          day_of_week: index,
                          start_time: '09:00',
                          end_time: '17:30',
                          is_active: index < 5
                        };
                        
                        return (
                          <div key={index} className={`flex items-center gap-3 p-3 rounded-lg ${dayHours.is_active ? 'bg-primary/5' : 'bg-muted/30'}`}>
                            <Switch
                              checked={dayHours.is_active}
                              onCheckedChange={(checked) => {
                                const newHours = widget.custom_work_hours.map(h => 
                                  h.day_of_week === index ? { ...h, is_active: checked } : h
                                );
                                setWidget({ ...widget, custom_work_hours: newHours });
                              }}
                            />
                            <span className={`w-20 font-medium ${!dayHours.is_active ? 'text-muted-foreground' : ''}`}>
                              {dayName}
                            </span>
                            {dayHours.is_active ? (
                              <>
                                <Input
                                  type="time"
                                  value={dayHours.start_time}
                                  onChange={(e) => {
                                    const newHours = widget.custom_work_hours.map(h => 
                                      h.day_of_week === index ? { ...h, start_time: e.target.value } : h
                                    );
                                    setWidget({ ...widget, custom_work_hours: newHours });
                                  }}
                                  className="w-28"
                                />
                                <span className="text-muted-foreground">à</span>
                                <Input
                                  type="time"
                                  value={dayHours.end_time}
                                  onChange={(e) => {
                                    const newHours = widget.custom_work_hours.map(h => 
                                      h.day_of_week === index ? { ...h, end_time: e.target.value } : h
                                    );
                                    setWidget({ ...widget, custom_work_hours: newHours });
                                  }}
                                  className="w-28"
                                />
                              </>
                            ) : (
                              <span className="text-sm text-muted-foreground">Fermé</span>
                            )}
                          </div>
                        );
                      })}
                      <p className="text-xs text-muted-foreground mt-2">
                        💡 Ces horaires s'appliquent uniquement à ce widget et remplacent les horaires généraux de l'entreprise.
                      </p>
                    </div>
                  )}

                  {!widget.use_custom_hours && (
                    <div className="p-4 bg-muted/30 rounded-lg text-sm text-muted-foreground">
                      <p>Les horaires de disponibilité généraux de l'entreprise seront utilisés.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Pricing Tab */}
        <TabsContent value="pricing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Mode de tarification</CardTitle>
              <CardDescription>Choisissez comment les prix sont calculés</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    widget.pricing_mode === 'hourly' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="pricing-mode-hourly"
                  onClick={() => setWidget({ ...widget, pricing_mode: 'hourly' })}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${widget.pricing_mode === 'hourly' ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />
                    <span className="font-semibold">Taux horaire unique</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Un tarif horaire appliqué à tous les services. Le prix est calculé selon la durée.
                  </p>
                </div>
                <div
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    widget.pricing_mode === 'per_service' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="pricing-mode-per-service"
                  onClick={() => setWidget({ ...widget, pricing_mode: 'per_service' })}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${widget.pricing_mode === 'per_service' ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />
                    <span className="font-semibold">Prix par service</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Définissez un prix fixe différent pour chaque service proposé.
                  </p>
                </div>
                <div
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    widget.pricing_mode === 'per_slot_type' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="pricing-mode-per-slot-type"
                  onClick={() => setWidget({ ...widget, pricing_mode: 'per_slot_type' })}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${widget.pricing_mode === 'per_slot_type' ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />
                    <span className="font-semibold">Prix fixe par créneau horaire</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Définissez un prix fixe pour chaque créneau (Matin, Après-midi, Journée…).
                  </p>
                </div>
              </div>

              {widget.pricing_mode === 'hourly' && (
                <div className="space-y-4 p-4 bg-muted/50 rounded-xl">
                  <div className="space-y-2">
                    <Label>Tarif horaire (€/h)</Label>
                    <Input
                      type="number"
                      min="1"
                      step="0.5"
                      data-testid="hourly-rate-input"
                      value={widget.hourly_rate}
                      onChange={(e) => setWidget({ ...widget, hourly_rate: parseFloat(e.target.value) || 0 })}
                      className="max-w-xs"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Exemple : Pour une demi-journée (4h) à {widget.hourly_rate}€/h = <span className="font-semibold">{(widget.hourly_rate * 4).toFixed(2)}€</span>
                  </p>
                </div>
              )}

              {widget.pricing_mode === 'per_service' && (
                <div className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <p className="text-sm font-medium mb-4">Prix par service activé</p>
                    {widget.enabled_services.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Aucun service activé. Sélectionnez des services dans l'onglet Services.</p>
                    ) : (
                      <div className="space-y-3">
                        {services.filter(s => widget.enabled_services.includes(s.id)).map((service) => (
                          <div key={service.id} className="flex items-center gap-4">
                            <Label className="flex-1 min-w-[150px]">{service.name}</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                data-testid={`service-price-${service.id}`}
                                value={widget.service_prices[service.id] || ''}
                                onChange={(e) => setWidget({
                                  ...widget,
                                  service_prices: {
                                    ...widget.service_prices,
                                    [service.id]: parseFloat(e.target.value) || 0
                                  }
                                })}
                                className="w-32"
                                placeholder="Prix"
                              />
                              <span className="text-sm text-muted-foreground">€</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {widget.pricing_mode === 'per_slot_type' && (
                <div className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <p className="text-sm font-medium mb-4">Prix fixe par créneau horaire activé</p>
                    {(widget.enabled_slot_types || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Aucun créneau activé. Activez des créneaux ci-dessous (Types de créneaux) pour leur définir un prix.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {slotTypes.filter(st => widget.enabled_slot_types.includes(st.id)).map((slotType) => (
                          <div key={slotType.id} className="flex items-center gap-4">
                            <div className="flex-1 min-w-[150px]">
                              <Label className="font-medium">{slotType.name}</Label>
                              <p className="text-xs text-muted-foreground">
                                {slotType.duration_hours}h
                                {slotType.time_slots && slotType.time_slots.length > 0 && (
                                  <> · {slotType.time_slots.map(ts => `${ts.start_time}-${ts.end_time}`).join(' | ')}</>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                data-testid={`slot-type-price-${slotType.id}`}
                                value={widget.slot_type_prices?.[slotType.id] ?? ''}
                                onChange={(e) => setWidget({
                                  ...widget,
                                  slot_type_prices: {
                                    ...(widget.slot_type_prices || {}),
                                    [slotType.id]: parseFloat(e.target.value) || 0
                                  }
                                })}
                                className="w-32"
                                placeholder="Prix"
                              />
                              <span className="text-sm text-muted-foreground">€</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    💡 Ce prix s'applique au niveau de ce widget et remplace le prix défini globalement dans <a href="/company/slot-types" className="text-primary underline">Types de créneaux</a>.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Types de créneaux - seulement pour widgets punctual et recurring */}
          {(widget.widget_type === 'punctual' || widget.widget_type === 'recurring') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Types de créneaux
                </CardTitle>
                <CardDescription>
                  Sélectionnez les créneaux à proposer à vos clients (Matin, Après-midi, Journée, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Option pour demander ou non le choix du créneau */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                  <div>
                    <Label className="text-base font-medium">Demander le choix du créneau</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Si désactivé, le client choisira uniquement le jour sans préciser le créneau horaire
                    </p>
                  </div>
                  <Switch
                    checked={widget.require_slot_selection}
                    onCheckedChange={(checked) => setWidget({ ...widget, require_slot_selection: checked })}
                  />
                </div>

                {widget.require_slot_selection && (
                  <>
                    {slotTypes.filter(st => st.is_active).length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="font-medium">Aucun type de créneau configuré</p>
                        <p className="text-sm mt-1">
                          Créez des types de créneaux dans{' '}
                          <a href="/company/slot-types" className="text-primary underline">
                            Mon entreprise → Types de créneaux
                          </a>
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm font-medium">Créneaux disponibles pour ce widget</Label>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const allIds = slotTypes.filter(st => st.is_active).map(st => st.id);
                              setWidget({ ...widget, enabled_slot_types: allIds });
                            }}
                          >
                            Tout sélectionner
                          </Button>
                        </div>
                        
                        {slotTypes.filter(st => st.is_active).map((slotType) => {
                          const isEnabled = widget.enabled_slot_types?.includes(slotType.id);
                          return (
                            <div
                              key={slotType.id}
                              className={`flex items-center justify-between p-4 border-2 rounded-xl transition-all cursor-pointer ${
                                isEnabled ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                              }`}
                              onClick={() => {
                                const newEnabled = isEnabled
                                  ? widget.enabled_slot_types.filter(id => id !== slotType.id)
                                  : [...(widget.enabled_slot_types || []), slotType.id];
                                setWidget({ ...widget, enabled_slot_types: newEnabled });
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox checked={isEnabled} />
                                <div>
                                  <div className="font-medium flex items-center gap-2">
                                    {slotType.name}
                                    <Badge variant="outline" className="text-xs">
                                      {slotType.duration_hours}h
                                    </Badge>
                                    {slotType.required_collaborators > 1 && (
                                      <Badge variant="secondary" className="text-xs">
                                        {slotType.required_collaborators} pers.
                                      </Badge>
                                    )}
                                  </div>
                                  {slotType.time_slots && slotType.time_slots.length > 0 && (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      {slotType.time_slots.map(ts => `${ts.name}: ${ts.start_time}-${ts.end_time}`).join(' | ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                {widget.pricing_mode === 'per_slot_type' ? (
                                  widget.slot_type_prices?.[slotType.id] != null ? (
                                    <div className="flex items-center gap-1 text-green-600 font-semibold">
                                      <Euro className="w-4 h-4" />
                                      {widget.slot_type_prices[slotType.id]}€
                                      <span className="text-xs text-muted-foreground font-normal ml-1">(forfait)</span>
                                    </div>
                                  ) : (
                                    <div className="text-xs text-amber-600">
                                      Définir le prix dans Tarifs
                                    </div>
                                  )
                                ) : slotType.pricing_mode === 'fixed' && slotType.fixed_price ? (
                                  <div className="flex items-center gap-1 text-green-600 font-semibold">
                                    <Euro className="w-4 h-4" />
                                    {slotType.fixed_price}€
                                    <span className="text-xs text-muted-foreground font-normal ml-1">(fixe)</span>
                                  </div>
                                ) : (
                                  <div className="text-sm text-muted-foreground">
                                    {slotType.duration_hours}h × {widget.hourly_rate}€ = {(slotType.duration_hours * widget.hourly_rate).toFixed(0)}€
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {widget.enabled_slot_types?.length === 0 && (
                          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                            ⚠️ Aucun créneau sélectionné. Les clients ne pourront pas choisir de créneau.
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Fields Tab */}
        {/* Services Tab */}
        <TabsContent value="services" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Services disponibles</CardTitle>
              <CardDescription>
                Sélectionnez des catégories entières ou des services individuels à proposer dans ce widget
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Categories Section */}
              {categories.length > 0 && (
                <div className="space-y-4">
                  <Label className="text-base font-medium">Par catégorie</Label>
                  <p className="text-sm text-muted-foreground">Sélectionnez une catégorie pour inclure tous ses services</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {categories.map((category) => {
                      const categoryServices = services.filter(s => s.category_id === category.id);
                      const isSelected = widget.enabled_categories?.includes(category.id);
                      return (
                        <div
                          key={category.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-all ${
                            isSelected
                              ? 'border-2 bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                          style={{ borderColor: isSelected ? category.color : undefined }}
                          onClick={() => {
                            if (isSelected) {
                              setWidget({
                                ...widget,
                                enabled_categories: widget.enabled_categories.filter(id => id !== category.id)
                              });
                            } else {
                              setWidget({
                                ...widget,
                                enabled_categories: [...(widget.enabled_categories || []), category.id]
                              });
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox checked={isSelected} />
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: category.color || '#84CC16' }} 
                            />
                            <span className="font-medium">{category.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {categoryServices.length} service{categoryServices.length > 1 ? 's' : ''}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Divider */}
              {categories.length > 0 && services.length > 0 && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">ou sélectionnez individuellement</span>
                  </div>
                </div>
              )}

              {/* Individual Services Section */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Services individuels</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {services.map((service) => {
                    // Check if service is included via category
                    const isIncludedViaCategory = widget.enabled_categories?.includes(service.category_id);
                    const isDirectlySelected = widget.enabled_services.includes(service.id);
                    const categoryInfo = categories.find(c => c.id === service.category_id);
                    
                    return (
                      <div
                        key={service.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          isIncludedViaCategory
                            ? 'border-primary/50 bg-primary/10 opacity-75'
                            : isDirectlySelected
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => {
                          if (isIncludedViaCategory) {
                            // Can't unselect if included via category
                            toast.info(`Ce service est inclus via la catégorie "${categoryInfo?.name}"`);
                            return;
                          }
                          if (isDirectlySelected) {
                            setWidget({
                              ...widget,
                              enabled_services: widget.enabled_services.filter(id => id !== service.id)
                            });
                          } else {
                            setWidget({
                              ...widget,
                              enabled_services: [...widget.enabled_services, service.id]
                            });
                          }
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={isDirectlySelected || isIncludedViaCategory} disabled={isIncludedViaCategory} />
                          <span className="font-medium text-sm">{service.name}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {categoryInfo && (
                            <Badge 
                              variant="outline" 
                              className="text-[10px] px-1.5 py-0"
                              style={{ borderColor: categoryInfo.color, color: categoryInfo.color }}
                            >
                              {categoryInfo.name}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {service.pricing_mode === 'fixed' 
                              ? `${service.fixed_price}€`
                              : service.pricing_mode === 'hourly'
                                ? `${service.price_per_hour}€/h`
                                : 'Info'
                            }
                          </span>
                        </div>
                        {isIncludedViaCategory && (
                          <p className="text-[10px] text-primary mt-1">✓ via catégorie</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Summary */}
              {(widget.enabled_categories?.length > 0 || widget.enabled_services.length > 0) && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm font-medium mb-2">Résumé de la sélection</p>
                  <div className="flex flex-wrap gap-2">
                    {widget.enabled_categories?.map(catId => {
                      const cat = categories.find(c => c.id === catId);
                      if (!cat) return null;
                      const catServices = services.filter(s => s.category_id === catId);
                      return (
                        <Badge 
                          key={catId} 
                          style={{ backgroundColor: cat.color, color: 'white' }}
                        >
                          {cat.name} ({catServices.length})
                        </Badge>
                      );
                    })}
                    {widget.enabled_services.filter(sId => {
                      const s = services.find(srv => srv.id === sId);
                      return s && !widget.enabled_categories?.includes(s.category_id);
                    }).length > 0 && (
                      <Badge variant="secondary">
                        +{widget.enabled_services.filter(sId => {
                          const s = services.find(srv => srv.id === sId);
                          return s && !widget.enabled_categories?.includes(s.category_id);
                        }).length} service(s) individuel(s)
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {services.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  Aucun service disponible. Créez d'abord des services dans l'onglet Services.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Questions Tab (previously Fields) */}
        <TabsContent value="questions" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Questions personnalisées</CardTitle>
                  <CardDescription>Configurez les questions posées au client lors de la réservation</CardDescription>
                </div>
                <Button onClick={addField} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une question
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {widget.fields.map((field, index) => {
                  const Icon = FIELD_ICONS[field.type] || Type;
                  const isNewlyAdded = field.field_id === newlyAddedFieldId;
                  return (
                    <div
                      key={field.field_id}
                      ref={isNewlyAdded ? newFieldRef : null}
                      className={`flex items-start gap-4 p-4 border rounded-lg transition-all duration-500 ${
                        isNewlyAdded 
                          ? 'bg-green-50 border-green-300 ring-2 ring-green-200 shadow-md' 
                          : 'bg-card'
                      }`}
                    >
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <GripVertical className="w-4 h-4 cursor-move" />
                        <Icon className="w-4 h-4" />
                        {isNewlyAdded && (
                          <Badge className="bg-green-500 text-white text-xs animate-pulse">Nouveau</Badge>
                        )}
                      </div>
                      
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Question</Label>
                          <Input
                            value={field.label}
                            onChange={(e) => updateField(index, { label: e.target.value })}
                            placeholder="Ex: Surface du terrain"
                          />
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">Type de réponse</Label>
                          <Select
                            value={field.type}
                            onValueChange={(value) => updateField(index, { type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map((ft) => (
                                <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">Étape</Label>
                          <Select
                            value={field.step || 'info'}
                            onValueChange={(value) => updateField(index, { step: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {widget.steps.filter(s => s.enabled).map((step) => (
                                <SelectItem key={step.step_id} value={step.step_id}>{step.title}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">Placeholder</Label>
                          <Input
                            value={field.placeholder || ''}
                            onChange={(e) => updateField(index, { placeholder: e.target.value })}
                            placeholder="Texte d'aide"
                          />
                        </div>
                        
                        <div className="flex items-end gap-4">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={field.required}
                              onCheckedChange={(checked) => updateField(index, { required: checked })}
                            />
                            <Label className="text-xs">Obligatoire</Label>
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeField(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Steps Tab */}
        <TabsContent value="steps" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Étapes du formulaire</CardTitle>
              <CardDescription>Activez ou désactivez les étapes du processus de réservation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {widget.steps.map((step, index) => (
                  <div
                    key={step.step_id}
                    className={`flex items-center gap-4 p-4 border rounded-lg transition-colors ${
                      step.enabled ? 'bg-card' : 'bg-muted/50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <GripVertical className="w-4 h-4 cursor-move" />
                      <Badge variant="outline">{index + 1}</Badge>
                    </div>
                    
                    <div className="flex-1">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Titre de l'étape</Label>
                          <Input
                            value={step.title}
                            onChange={(e) => updateStep(index, { title: e.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={step.description || ''}
                            onChange={(e) => updateStep(index, { description: e.target.value })}
                            placeholder="Description de l'étape"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Activée</Label>
                      <Switch
                        checked={step.enabled}
                        onCheckedChange={(checked) => updateStep(index, { enabled: checked })}
                        disabled={step.step_id === 'confirm'} // Confirmation always enabled
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Message de confirmation</CardTitle>
              <CardDescription>Message affiché après la soumission du formulaire</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Titre</Label>
                <Input
                  value={widget.confirmation_title || ''}
                  onChange={(e) => setWidget({ ...widget, confirmation_title: e.target.value })}
                  placeholder="Merci pour votre réservation !"
                />
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  value={widget.confirmation_message || ''}
                  onChange={(e) => setWidget({ ...widget, confirmation_message: e.target.value })}
                  placeholder="Votre demande a bien été enregistrée..."
                  rows={4}
                />
              </div>
              <div className="space-y-2">
                <Label>Texte du bouton de soumission</Label>
                <Input
                  value={widget.submit_button_text || ''}
                  onChange={(e) => setWidget({ ...widget, submit_button_text: e.target.value })}
                  placeholder="Confirmer la réservation"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Terms Tab */}
        <TabsContent value="terms" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Conditions Générales</CardTitle>
                  <CardDescription>Faites valider vos CGV avant la réservation</CardDescription>
                </div>
                <Switch
                  checked={widget.terms_enabled}
                  onCheckedChange={(checked) => setWidget({ ...widget, terms_enabled: checked })}
                />
              </div>
            </CardHeader>
            {widget.terms_enabled && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Texte de la case à cocher</Label>
                  <Textarea
                    value={widget.terms_text || ''}
                    onChange={(e) => setWidget({ ...widget, terms_text: e.target.value })}
                    placeholder="J'accepte les conditions générales de vente et la politique de confidentialité."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lien vers les CGV (optionnel)</Label>
                  <Input
                    value={widget.terms_link || ''}
                    onChange={(e) => setWidget({ ...widget, terms_link: e.target.value })}
                    placeholder="https://votresite.com/cgv"
                  />
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-2">Aperçu :</p>
                  <div className="flex items-start gap-2">
                    <Checkbox disabled />
                    <span className="text-sm">
                      {widget.terms_text || "J'accepte les conditions générales de vente."}
                      {widget.terms_link && (
                        <a href={widget.terms_link} className="text-primary underline ml-1" target="_blank" rel="noopener noreferrer">
                          Voir les CGV
                        </a>
                      )}
                    </span>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* Style Tab */}
        <TabsContent value="style" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Couleurs personnalisées</CardTitle>
              <CardDescription>Adaptez les couleurs à votre charte graphique</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <Label>Couleur primaire</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={widget.primary_color}
                      onChange={(e) => setWidget({ ...widget, primary_color: e.target.value })}
                      className="w-12 h-10 rounded-lg border cursor-pointer"
                    />
                    <Input
                      value={widget.primary_color}
                      onChange={(e) => setWidget({ ...widget, primary_color: e.target.value })}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Couleur secondaire</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={widget.secondary_color}
                      onChange={(e) => setWidget({ ...widget, secondary_color: e.target.value })}
                      className="w-12 h-10 rounded-lg border cursor-pointer"
                    />
                    <Input
                      value={widget.secondary_color}
                      onChange={(e) => setWidget({ ...widget, secondary_color: e.target.value })}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Couleur de fond</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={widget.bg_color}
                      onChange={(e) => setWidget({ ...widget, bg_color: e.target.value })}
                      className="w-12 h-10 rounded-lg border cursor-pointer"
                    />
                    <Input
                      value={widget.bg_color}
                      onChange={(e) => setWidget({ ...widget, bg_color: e.target.value })}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Couleur du texte</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={widget.text_color}
                      onChange={(e) => setWidget({ ...widget, text_color: e.target.value })}
                      className="w-12 h-10 rounded-lg border cursor-pointer"
                    />
                    <Input
                      value={widget.text_color}
                      onChange={(e) => setWidget({ ...widget, text_color: e.target.value })}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo Display Option */}
          <Card>
            <CardHeader>
              <CardTitle>Logo de l'entreprise</CardTitle>
              <CardDescription>Affichez votre logo en haut du widget</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Afficher le logo</Label>
                  <p className="text-sm text-muted-foreground">Le logo de votre entreprise sera affiché en grand en haut du widget</p>
                </div>
                <Switch
                  checked={widget.show_logo !== false}
                  onCheckedChange={(checked) => setWidget({ ...widget, show_logo: checked })}
                  data-testid="show-logo-switch"
                />
              </div>
              
              {/* Hide duration choice option - only for punctual and recurring widgets */}
              {(widget.widget_type === 'punctual' || widget.widget_type === 'recurring') && (
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-1">
                    <Label>Masquer le choix de durée</Label>
                    <p className="text-sm text-muted-foreground">
                      Le client ne choisira pas entre demi-journée/journée complète. Vous estimerez la durée lors du traitement de la demande.
                    </p>
                  </div>
                  <Switch
                    checked={widget.hide_duration_choice === true}
                    onCheckedChange={(checked) => setWidget({ ...widget, hide_duration_choice: checked })}
                    data-testid="hide-duration-switch"
                  />
                </div>
              )}

              {/* Expose this widget in the connected client space */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg mt-3">
                <div className="space-y-1">
                  <Label>Afficher dans l'espace client</Label>
                  <p className="text-sm text-muted-foreground">
                    Vos clients connectés verront ce widget comme une carte de réservation dans leur espace personnel (en plus de l'intégration publique).
                  </p>
                </div>
                <Switch
                  checked={widget.show_in_client_space !== false}
                  onCheckedChange={(checked) => setWidget({ ...widget, show_in_client_space: checked })}
                  data-testid="show-in-client-space-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Live Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Aperçu</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="rounded-xl p-6 min-h-[200px]"
                style={{ backgroundColor: widget.bg_color, color: widget.text_color }}
              >
                <h3 className="text-xl font-semibold mb-2">{widget.name || 'Mon Widget'}</h3>
                <p className="opacity-70 mb-4">{widget.description || 'Description du widget'}</p>
                
                <div className="space-y-3">
                  <button 
                    className="w-full py-2 px-4 rounded-full font-medium text-white"
                    style={{ backgroundColor: widget.primary_color }}
                  >
                    {widget.submit_button_text || 'Confirmer'}
                  </button>
                  <button 
                    className="w-full py-2 px-4 rounded-full font-medium text-white"
                    style={{ backgroundColor: widget.secondary_color }}
                  >
                    Bouton secondaire
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
